import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface CookieConsentContextType {
  hasConsented: boolean | null;
  consentToCookies: () => void;
  denyCookies: () => void;
  showBanner: boolean;
  setShowBanner: (show: boolean) => void;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

interface CookieConsentProviderProps {
  children: ReactNode;
}

export const CookieConsentProvider: React.FC<CookieConsentProviderProps> = ({ children }) => {
  const [hasConsented, setHasConsented] = useState<boolean | null>(null);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consentStatus = localStorage.getItem('cookieConsent');
    if (consentStatus === null) {
      // No choice made yet, show banner
      setShowBanner(true);
    } else {
      // User has already made a choice
      setHasConsented(consentStatus === 'accepted');
      setShowBanner(false);
    }
  }, []);

  const consentToCookies = () => {
    setHasConsented(true);
    setShowBanner(false);
    localStorage.setItem('cookieConsent', 'accepted');
    
    // Enable analytics and other cookies here
    // For example, if you're using Google Analytics:
    // gtag('consent', 'update', { 'analytics_storage': 'granted' });
  };

  const denyCookies = () => {
    setHasConsented(false);
    setShowBanner(false);
    localStorage.setItem('cookieConsent', 'denied');
    
    // Disable analytics and other cookies here
    // For example, if you're using Google Analytics:
    // gtag('consent', 'update', { 'analytics_storage': 'denied' });
  };

  const value: CookieConsentContextType = {
    hasConsented,
    consentToCookies,
    denyCookies,
    showBanner,
    setShowBanner,
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};

// Export the context for use in the hook
export { CookieConsentContext }; 